import React, { useEffect, useState } from 'react'
import { Capacitor, PluginListenerHandle } from '@capacitor/core'
import { useActivityTracker } from '../../hooks/useActivityTracker'
import YumButton from '../../components/YumButton/YumButton'
import OfflineStatusBadge from '../../components/OfflineStatusBadge/OfflineStatusBadge'
import { CustomTypeCategory } from '../../services/CustomTypes'
import { WorkoutType } from '../../plugins/ActivityTrackerPlugin'
import { Units } from '../../services/api.types'
import { isRtl } from '../../i18nInit'
import { c, t } from 'ttag'
import ChallengeActivitySelect, {
  IChallengeActivitySelectProps,
} from '../ChallengeAdmin/components/ChallengeActivitySelect/ChallengeActivitySelect'
import { Keyboard } from '@capacitor/keyboard'
import { scrollToBottom } from '../../common/helpers/scroll'
import { getWorkoutActivities, getWorkoutLabel } from '../../common/workoutActivities'

interface WorkoutTrackerProps {
  /** Optional callback to refresh activities after successful upload */
  onActivityUploaded?: () => void
  /** User profile data from Redux store */
  userDto: any
}

// Mapping from supported CustomTypeCategory to WorkoutType
// const getWorkoutType = (activityType: CustomTypeCategory): WorkoutType => {
//   switch (activityType) {
//     case CustomTypeCategory.WALKING:
//       return 'walking'
//     case CustomTypeCategory.RUNNING:
//       return 'running'
//     case CustomTypeCategory.CYCLING:
//       return 'cycling'
//     case CustomTypeCategory.YOGA:
//       return 'yoga'
//     case CustomTypeCategory.SWIMMING:
//       return 'swimming'
//     default:
//       return 'other'
//   }
// }

const WorkoutTracker: React.FC<WorkoutTrackerProps> = ({ onActivityUploaded, userDto }) => {
  const { startWorkout, isLoading, error, successMessage, uploadStats, retryOfflineUploads, clearOfflineQueue } =
    useActivityTracker()

  // State for activity selection
  const [isSelectingActivity, setIsSelectingActivity] = useState(false)
  const [selectedActivityType, setSelectedActivityType] = useState<string>('walking')

  // Send metric labels to Swift plugin when component mounts or language changes
  React.useEffect(() => {
    const sendMetricLabels = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Get user's preferred units (default to METRIC if not available)
          const userUnits = userDto?.units || Units.METRIC
          const isImperial = userUnits === Units.IMPERIAL

          // Create labels mapping with metric labels and units based on user preference
          const labelsMapping: Record<string, string> = {
            // Metric labels
            time: t`Time`,
            distance: t`Distance`,
            speed: t`Speed`,
            pace: t`Avg Pace`,
            steps: t`Steps`,
            calories: t`Calories`,
            // Units based on user preference
            time_unit: '',
            distance_unit: isImperial ? t`mi` : t`km`,
            speed_unit: isImperial ? t`mi/h` : t`km/h`,
            pace_unit: isImperial ? t`min/mi` : t`min/km`,
            steps_unit: t`total`,
            calories_unit: t`kcal`,
            // Send user's unit preference to Swift
            user_units: isImperial ? 'IMPERIAL' : 'METRIC',
          }

          // Import ActivityTracker plugin dynamically to avoid circular imports
          const { default: ActivityTracker } = await import('../../plugins/ActivityTrackerPlugin')

          // Send metric labels to Swift plugin
          await ActivityTracker.setActivityLabels({ labels: labelsMapping })
          console.log('✅ Sent metric labels to Swift plugin:', labelsMapping)
        } catch (error) {
          console.error('❌ Failed to send metric labels to Swift plugin:', error)
        }
      }
    }

    sendMetricLabels()
  }, [userDto?.units]) // Re-run when user units change

  // Send translated labels (activity + metrics) to Swift plugin
  const sendTranslatedLabels = async (activityType: string) => {
    if (Capacitor.isNativePlatform()) {
      try {
        // Find the selected activity and get its translated label
        const selectedActivity = getWorkoutActivities().find((activity) => activity.value === activityType)
        if (selectedActivity) {
          // Get user's preferred units (default to METRIC if not available)
          const userUnits = userDto?.units || Units.METRIC
          const isImperial = userUnits === Units.IMPERIAL

          // Create labels mapping with activity, metric labels, and units based on user preference
          const labelsMapping: Record<string, string> = {
            // Activity label
            [activityType]: selectedActivity.label,
            // Metric labels
            time: t`Time`,
            distance: t`Distance`,
            speed: t`Speed`,
            pace: t`Avg Pace`,
            steps: t`Steps`,
            calories: t`Calories`,
            // Units based on user preference
            time_unit: '',
            distance_unit: isImperial ? t`mi` : t`km`,
            speed_unit: isImperial ? t`mi/h` : t`km/h`,
            pace_unit: isImperial ? t`min/mi` : t`min/km`,
            steps_unit: t`total`,
            calories_unit: t`kcal`,
            // Send user's unit preference to Swift
            user_units: isImperial ? 'IMPERIAL' : 'METRIC',
          }

          // Import ActivityTracker plugin dynamically to avoid circular imports
          const { default: ActivityTracker } = await import('../../plugins/ActivityTrackerPlugin')

          // Send translated labels to Swift plugin
          await ActivityTracker.setActivityLabels({ labels: labelsMapping })
          console.log('✅ Sent translated labels to Swift plugin:', labelsMapping)
        }
      } catch (error) {
        console.error('❌ Failed to send translated labels to Swift plugin:', error)
      }
    }
  }

  // Show activity selection when Record button is pressed
  const handleShowActivitySelector = () => {
    // if (!Capacitor.isNativePlatform()) {
    //   alert('Native workout tracker only works on native platforms')
    //   return
    // }
    setIsSelectingActivity(true)
  }

  // Handle activity selection and start workout immediately
  const handleActivitySelect = async (activityType: string) => {
    setSelectedActivityType(activityType)
    console.log('🎯 Activity selected:', activityType)

    // Send the translated labels (activity + metrics) to Swift plugin
    await sendTranslatedLabels(activityType)

    console.log('🏃‍♂️ Starting workout immediately...', {
      selectedActivity: activityType,
      platform: Capacitor.getPlatform(),
    })

    try {
      // Convert string activity type to WorkoutType
      const workoutType = activityType as WorkoutType
      await startWorkout(workoutType)
      setIsSelectingActivity(false) // Close activity selector on successful start
      console.log('✅ Workout started successfully')
    } catch (error: any) {
      console.error('❌ Failed to start workout:', error)
      console.error('❌ Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
        toString: error?.toString(),
      })

      // Check if it's an iOS version compatibility issue
      if (error?.message?.includes('iOS 15.0') || error?.message?.includes('iOS version')) {
        alert('Native workout tracker requires iOS 15.0 or later. Please update your device or use the web version.')
      } else {
        // Show a user-friendly error message
        alert(`Failed to start workout: ${error?.message || 'Unknown error'}`)
      }
    }
  }

  // Cancel activity selection
  const handleCancelSelection = () => {
    setIsSelectingActivity(false)
  }

  // Convert Swift WorkoutType to React WorkoutType
  /*const convertToReactWorkoutType = (workoutType: WorkoutType): ReactWorkoutType => {
    switch (workoutType) {
      case 'walking' as WorkoutType:
        return ReactWorkoutType.WALKING
      case 'running' as WorkoutType:
        return ReactWorkoutType.RUNNING
      case 'cycling' as WorkoutType:
        return ReactWorkoutType.CYCLING
      case 'swimming' as WorkoutType:
        return ReactWorkoutType.SWIMMING
      case 'yoga' as WorkoutType:
        return ReactWorkoutType.YOGA
      case 'other' as WorkoutType:
      default:
        return ReactWorkoutType.OTHER
    }
  }*/

  // Convert React WorkoutType to Swift WorkoutType
  /*const convertToSwiftWorkoutType = (workoutType: ReactWorkoutType): WorkoutType => {
    switch (workoutType) {
      case ReactWorkoutType.WALKING:
        return 'walking' as WorkoutType
      case ReactWorkoutType.RUNNING:
        return 'running' as WorkoutType
      case ReactWorkoutType.CYCLING:
        return 'cycling' as WorkoutType
      case ReactWorkoutType.SWIMMING:
        return 'swimming' as WorkoutType
      case ReactWorkoutType.YOGA:
        return 'yoga' as WorkoutType
      case ReactWorkoutType.OTHER:
      default:
        return 'other' as WorkoutType
    }
  }*/

  // Note: Workout confirmation is now handled globally by GlobalWorkoutConfirmationModal

  // Show error message if there's an error state
  React.useEffect(() => {
    if (error) {
      console.error('❌ Activity tracker error:', error)
    }
  }, [error])

  // Show success message when activity is uploaded and refresh activities
  React.useEffect(() => {
    if (successMessage) {
      console.log('✅ Activity tracker success:', successMessage)
      // Refresh the activities list to show the new activity
      if (onActivityUploaded) {
        onActivityUploaded()
      }
    }
    // Fixed: Only depend on successMessage to avoid infinite loop from onActivityUploaded function reference changes
  }, [successMessage])

  return (
    <>
      {/* Recording relies on the native activity-tracker plugin, so the Record button and its
          offline upload status are only shown on native platforms (hidden on web). */}
      {Capacitor.isNativePlatform() && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <YumButton
            size="small"
            color="secondary"
            onClick={handleShowActivitySelector}
            disabled={isLoading}
            style={{ marginLeft: isRtl ? 0 : '10px', marginRight: isRtl ? '10px' : 0 }}
          >
            {isLoading ? t`Starting...` : t`Record`}
          </YumButton>

          {/* Enhanced offline status badge */}
          <OfflineStatusBadge
            uploadStats={uploadStats}
            onRetry={retryOfflineUploads}
            onClear={clearOfflineQueue}
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Activity Selection - Direct ChallengeActivitySelect */}
      {isSelectingActivity && (
        <ChallengeActivitySelect
          selectedActivity={selectedActivityType}
          onSelect={handleActivitySelect}
          onClose={handleCancelSelection}
          showModalOnly={true}
          submitButtonLabel={t`Start`}
          customActivities={getWorkoutActivities().filter((activity) => !activity.hideFromList)}
        />
      )}

      {/* Note: Workout confirmation is now handled globally by GlobalWorkoutConfirmationModal */}
    </>
  )
}

export default WorkoutTracker
